import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { resolvePostLoginDest } from "../lib/roleRedirect";
import { Sparky } from "../components/Sparky";
import { Header } from "../components/Header";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await api.login({ email, password });
      setUser(data.user);
      navigate(resolvePostLoginDest(next, data.user), { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="login-page">
      <Header />
      <div className="mx-auto max-w-6xl px-6 md:px-10 py-12 md:py-16 grid md:grid-cols-2 gap-12 items-center">
        <div className="hidden md:block">
          <div className="relative">
            <div
              aria-hidden
              className="absolute inset-0 rounded-[3rem] -rotate-3"
              style={{ background: "linear-gradient(135deg,#4CC9F0,#4361EE)" }}
            />
            <div className="relative rounded-[3rem] bg-white p-8 border-2 border-black/5 shadow-[0_30px_60px_rgba(0,0,0,0.10)]">
              <div className="animate-float">
                <Sparky size={260} />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight" data-testid="login-title">
            {t("auth.loginTitle")}
          </h1>
          <p className="mt-2 text-black/60 font-medium">{t("auth.loginSub")}</p>

          <button
            onClick={googleLogin}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-white border-2 border-black/10 hover:border-black/30 transition font-bold px-6 py-4 shadow-[0_3px_0_rgba(0,0,0,0.08)]"
            data-testid="login-google-btn"
          >
            <GoogleIcon />
            <span>{t("auth.google")}</span>
          </button>

          <div className="my-6 flex items-center gap-4 text-black/40 text-sm">
            <div className="h-px flex-1 bg-black/10" />
            <span>{t("auth.or")}</span>
            <div className="h-px flex-1 bg-black/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-black/70">{t("auth.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-2xl border-2 border-black/10 focus:border-[#4361EE] focus:ring-0 outline-none px-4 py-3 text-base font-medium bg-white"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-black/70">{t("auth.password")}</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-2xl border-2 border-black/10 focus:border-[#4361EE] focus:ring-0 outline-none px-4 py-3 text-base font-medium bg-white"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div
                className="rounded-2xl bg-[#FEE2E2] text-[#991B1B] px-4 py-3 text-sm font-semibold"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 disabled:opacity-60 text-white font-bold px-6 py-4 shadow-[0_4px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_3px_0_#2A3EB1] transition"
              data-testid="login-submit-btn"
            >
              {busy ? "…" : t("auth.submitLogin")}
            </button>
          </form>

          <p className="mt-6 text-sm text-black/60">
            {t("auth.noAccount")}{" "}
            <Link to="/signup" className="font-bold text-[#4361EE] hover:underline" data-testid="login-to-signup">
              {t("auth.goSignup")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
      <path fill="#4285F4" d="M19.6 10.23c0-.68-.06-1.36-.19-2H10v3.79h5.4a4.62 4.62 0 0 1-2 3.03v2.5h3.23c1.9-1.75 2.97-4.33 2.97-7.32Z" />
      <path fill="#34A853" d="M10 20c2.7 0 4.97-.9 6.63-2.44l-3.23-2.5c-.9.6-2.05.96-3.4.96-2.62 0-4.84-1.77-5.63-4.15H1.03v2.6A10 10 0 0 0 10 20Z" />
      <path fill="#FBBC05" d="M4.37 11.87A6 6 0 0 1 4.06 10c0-.65.11-1.28.31-1.87V5.53H1.03A10 10 0 0 0 0 10c0 1.6.38 3.1 1.03 4.47l3.34-2.6Z" />
      <path fill="#EA4335" d="M10 3.98c1.47 0 2.79.51 3.83 1.5l2.86-2.86C14.96.98 12.7 0 10 0A10 10 0 0 0 1.03 5.53l3.34 2.6C5.16 5.75 7.38 3.98 10 3.98Z" />
    </svg>
  );
}
